/** The asset page's Storage tab: every representation, where each copy lives, transfers and restores. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Cloud, HardDrive, Loader2, Server, Trash2, X } from 'lucide-react';
import type { AssetDetail } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useMe, useStorage, useTransfers } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { Availability } from '../components/Status';
import { RESTORE_STATUS_LABEL, useRestores } from '../components/ArchiveRestore';
import { toast } from '../components/Toasts';
import { bytes, dateTime, relative } from '../lib/format';
import { JobProgress } from '../components/JobProgress';
import { LoadMore } from '../components/LoadMore';

export function StorageTab({ asset }: { asset: AssetDetail }) {
  if (asset.status === 'placeholder') return <p className="muted" data-testid="storage-tab">No media yet, so nothing is stored for this asset.</p>;
  return <StorageBody asset={asset} />;
}

function StorageBody({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const storage = useStorage();
  const transfers = useTransfers(asset.id);
  const restores = useRestores(asset.id);
  const qc = useQueryClient();
  const [copying, setCopying] = useState<string | null>(null);
  const canTransfer = me.permissions.includes('storage.manage');
  const central = (storage.data?.items ?? []).filter((l) => l.kind !== 'agent' && l.enabled && l.purpose === 'storage');

  async function requestCopy(representationId: string, destinationLocationId: string) {
    setCopying(representationId);
    try {
      await api('/api/v1/transfers', { method: 'POST', json: { representationId, destinationLocationId } });
      await qc.invalidateQueries({ queryKey: queryKeys.transfers(asset.id) });
      toast('Transfer started');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setCopying(null);
    }
  }

  async function deleteCopy(r: AssetDetail['representations'][number], l: AssetDetail['representations'][number]['locations'][number]) {
    const present = r.locations.filter((x) => x.status === 'present').length;
    const last = present === 1;
    if (last && r.kind === 'original') {
      toast('This is the only copy of the original. To remove it everywhere, delete the asset.', 'error');
      return;
    }
    if (last && r.kind === 'master') {
      toast('This is the only copy of this master. To remove it, use Remove on the Masters tab.', 'error');
      return;
    }
    const minDays = l.archive ? (storage.data?.items ?? []).find((x) => x.id === l.storageLocationId)?.archive?.minimumDays : null;
    const msg = (last
      ? `Delete the only copy of the ${r.kind} (in “${l.storageName}”)? It can be made again by reprocessing the asset.`
      : `Delete the ${r.kind} copy in “${l.storageName}”? ${present - 1} other cop${present - 1 === 1 ? 'y stays' : 'ies stay'}.`)
      + (minDays ? ` “${l.storageName}” is an archive that bills at least ${minDays} days per file: deleting it earlier is charged as if it stayed.` : '');
    if (!window.confirm(msg)) return;
    try {
      await api(`/api/v1/assets/${asset.id}/files/${r.id}/locations/${l.storageLocationId}${last ? '?lastCopy=true' : ''}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.asset(asset.id) });
      toast(`Deleted the copy in ${l.storageName}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div data-testid="storage-tab">
      <p className="muted" style={{ marginTop: 0 }}>Every representation of version {asset.versionNumber}, and exactly where each copy lives. Originals are preserved; derivatives can be regenerated. Copies between locations happen only when requested, and every transfer is verified and recorded.</p>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }} data-testid="storage-last-used" title={asset.lastAccessedAt ? dateTime(asset.lastAccessedAt) : undefined}>
        Last used: {asset.lastAccessedAt ? relative(asset.lastAccessedAt) : 'never (tiering rules count from when each file arrived)'}.
        <span className="faint"> Playback, downloads, exports and processing of version {asset.versionNumber} count, for all of its files; tiering moves don't.</span>
      </p>
      {asset.representations.map((r) => {
        const present = new Set(r.locations.filter((l) => l.status === 'present').map((l) => l.storageLocationId));
        // Further masters (1.2.0) are originals too: stored, tiered and archived like the primary.
        const isOriginal = r.kind === 'original' || r.kind === 'master';
        const onSite = isOriginal ? (storage.data?.items ?? []).filter((l) => l.enabled && l.purpose === 'storage' && (l.agentRoot?.mode === 'managed' || l.agentFolder?.mode === 'managed')) : [];
        // Archives (0.29.0) hold originals only.
        const targets = [...central, ...onSite].filter((l) => !present.has(l.id) && (isOriginal || !l.archive?.cold));
        const active = transfers.data?.items.find((t) => t.representationId === r.id && (t.status === 'queued' || t.status === 'running'));
        return (
          <div className="rep-card" key={r.id} data-kind={r.kind}>
            <div className="rep-head">
              <span className="rep-kind">{r.kind}</span>
              <span className="pill">{r.preset}</span>
              {r.kind === 'master' && typeof r.attributes.formatLabel === 'string' && <span className="faint" data-testid="storage-master-format">{r.attributes.formatLabel}</span>}
              <span style={{ flex: 1 }} />
              <span className="muted">{bytes(r.sizeBytes)}</span>
              {canTransfer && targets.length > 0 && !active && (
                <select className="speed-select" aria-label={`Copy ${r.kind} to another location`} value="" disabled={copying === r.id}
                  onChange={(e) => e.target.value && void requestCopy(r.id, e.target.value)}>
                  <option value="">Copy to…</option>
                  {targets.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
                </select>
              )}
            </div>
            {r.checksumSha256 && <div className="mono faint" style={{ fontSize: 11 }} title="SHA-256">sha256 {r.checksumSha256.slice(0, 16)}…{r.checksumSha256.slice(-8)}</div>}
            {r.locations.map((l) => (
              <div className="loc-row" key={l.storageLocationId}>
                {l.storageKind === 's3' || l.storageKind === 'oci' ? <Cloud size={14} color="var(--muted)" /> : l.storageKind === 'agent' ? <Server size={14} color="var(--muted)" /> : <HardDrive size={14} color="var(--muted)" />}
                <strong style={{ fontWeight: 550 }}>{l.storageName}</strong>
                <span className="pill">{l.site}</span>
                {!l.managed && <span className="pill" title="PRISM catalogues this file where it already lives and never moves or modifies it">In place</span>}
                <span className="key" title={l.key}>{l.key}</span>
                {l.archive && (
                  <span className="archive-badge" data-testid="copy-archive-state" data-state={l.archive.state}
                    title={l.archive.state === 'available' ? 'Restored: readable for now, then archived again' : l.archive.state === 'restoring' ? 'A restore was requested' : 'Archived: restore before reading'}>
                    {l.archive.state === 'archived' ? 'Archived' : l.archive.state === 'restoring' ? 'Restoring…' : `Restored${l.archive.expiresAt ? ` until ${new Date(l.archive.expiresAt).toLocaleString()}` : ''}`}
                  </span>
                )}
                {l.status === 'missing' ? <span className="status failed">Missing</span> : <Availability value={l.availability} />}
                {canTransfer && l.status === 'present' && l.managed && !asset.deleted && (
                  <button className="btn ghost icon sm danger" aria-label={`Delete the ${r.kind} copy in ${l.storageName}`} title="Delete this copy" data-testid="delete-copy"
                    onClick={() => void deleteCopy(r, l)}><Trash2 size={12} /></button>
                )}
              </div>
            ))}
            {active && <div className="loc-row"><Loader2 size={14} className="spin" /> <span className="muted">Copying to {active.destinationName}…</span></div>}
          </div>
        );
      })}
      {(restores.data?.items.length ?? 0) > 0 && (
        <div style={{ marginTop: 18 }}>
          <p className="section-label">Restores from archives</p>
          <div className="steps" data-testid="restores">
            {restores.data!.items.map((x) => (
              <div className="step" key={x.id} data-status={x.status}>
                <span className={`step-icon ${x.status === 'succeeded' ? 'succeeded' : x.status === 'failed' ? 'failed' : 'running'}`}>
                  {x.status === 'succeeded' ? <Check size={12} strokeWidth={3} /> : x.status === 'failed' ? <X size={12} strokeWidth={3} /> : <Loader2 size={12} className="spin" />}
                </span>
                <div className="step-main">
                  <span className="step-title">{x.source.name} → {x.destination.name} · {RESTORE_STATUS_LABEL[x.status]}</span>
                  <span className="step-sub">{x.requestedBy ?? 'PRISM'} · {({ manual: 'Restore', export: 'Restore and export', workflow: 'Restore and start' })[x.reason]}{x.tier ? ` · ${x.tier} retrieval` : ''} · readable for {x.days} day{x.days === 1 ? '' : 's'}</span>
                  {x.error && <span className="step-sub" style={{ color: 'var(--danger)' }}>{x.error.message}</span>}
                </div>
                <span className="step-time">{relative(x.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {(transfers.data?.items.length ?? 0) > 0 && (
        <div style={{ marginTop: 18 }}>
          <p className="section-label">Transfers</p>
          <div className="steps" data-testid="transfers">
            {transfers.data!.items.map((t) => (
              <div className="step" key={t.id}>
                {/* Cancelled (1.1.0): by a storage migration's pause or window closing, or by its cancel; it is not an error. */}
                <span className={`step-icon ${t.status === 'succeeded' ? 'succeeded' : t.status === 'failed' ? 'failed' : t.status === 'cancelled' ? 'skipped' : 'running'}`} data-status={t.status}>
                  {t.status === 'succeeded' ? <Check size={12} strokeWidth={3} /> : t.status === 'failed' || t.status === 'cancelled' ? <X size={12} strokeWidth={3} /> : <Loader2 size={12} className="spin" />}
                </span>
                <div className="step-main">
                  <span className="step-title">{t.representationKind}: {t.sourceName} → {t.destinationName}{t.status === 'cancelled' ? <span className="faint" data-testid="transfer-cancelled"> · Cancelled</span> : null}</span>
                  <span className="step-sub">{t.requestedBy ?? 'PRISM'}{t.reason ? ` · ${t.reason}` : ''}{t.bytes && t.status === 'succeeded' ? ` · ${bytes(t.bytes)} verified` : t.bytes && t.status === 'running' ? ` · ${bytes(t.bytes)} copied` : ''}</span>
                  {(t.status === 'queued' || t.status === 'running') && <JobProgress active waiting={t.waiting} waitingSince={t.waitingSince} step="transfer" percent={t.percent} />}
                  {t.error && <span className="step-sub" style={t.status === 'cancelled' ? undefined : { color: 'var(--danger)' }}>{t.error.message}</span>}
                </div>
                <span className="step-time">{relative(t.createdAt)}</span>
              </div>
            ))}
          </div>
          <LoadMore list={transfers} shown={transfers.data!.items.length} noun="transfers" />
        </div>
      )}
    </div>
  );
}
